import React, { useState, useCallback } from 'react';
import {
  Card,
  Select,
  Button,
  Table,
  Tag,
  message,
  Spin,
  Space,
  Input,
  Modal,
  Avatar,
  Collapse,
  Tooltip,
  Alert,
  Progress,
  Form,
  Row
} from 'antd';
import {
  SyncOutlined,
  SearchOutlined,
  LinkOutlined,
  PlusOutlined,
  EditOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloudDownloadOutlined,
  GlobalOutlined,
  UserOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  StopOutlined,
  UndoOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import dayjs from 'dayjs';
import apiClient from '../../server/server';
import { fetchProfiles, getProfileById, searchProfilesMeili } from '../../server/profiles/profilesServer';
import { rescrapeProfile, updateProfileScrapSource, fetchTixrGroupEvents, detectSourceType } from '../../server/scraper/raScraperServer';
import EventFormSection from './EventFormSection';

const { Option } = Select;

const EventSyncTab = ({ countries = [], searchProfiles, musicGenres = [], profilesLoading: externalProfilesLoading = false }) => {
  // City filter state
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [cities, setCities] = useState([]);

  // Profiles state
  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  // Sync state
  const [syncingProfileId, setSyncingProfileId] = useState(null);
  const [syncResults, setSyncResults] = useState({}); // keyed by profileId

  // Results filter
  const [eventSearch, setEventSearch] = useState('');
  const [showOnlyMissing, setShowOnlyMissing] = useState(true);

  // ScrapSource edit state
  const [editingProfileId, setEditingProfileId] = useState(null);
  const [editSourceUrl, setEditSourceUrl] = useState('');
  const [savingSource, setSavingSource] = useState(false);

  // Import state
  const [importingEventUrl, setImportingEventUrl] = useState(null);
  const [fastImportingEventUrl, setFastImportingEventUrl] = useState(null);
  const [fastUnfinishedEventUrl, setFastUnfinishedEventUrl] = useState(null);

  // Sync All state
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncAllProgress, setSyncAllProgress] = useState({ current: 0, total: 0 });

  // Fast All state
  const [fastAllRunning, setFastAllRunning] = useState(false);
  const [fastAllMode, setFastAllMode] = useState(null); // 'publish' or 'unfinished'
  const [fastAllProgress, setFastAllProgress] = useState({ current: 0, total: 0 });

  // Skipped events state
  const [skippedEvents, setSkippedEvents] = useState(new Set());

  // Skipped profiles state (for Sync All)
  const [skippedProfiles, setSkippedProfiles] = useState(new Set());

  // Profile search filter
  const [profileSearch, setProfileSearch] = useState('');

  // ── Event Form State ──
  const [form] = Form.useForm();
  const [formLoading, setFormLoading] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [formSelectedProfile, setFormSelectedProfile] = useState(null);
  const [formSearchResults, setFormSearchResults] = useState([]);
  const [formSearchLoading, setFormSearchLoading] = useState(false);
  const [formCities, setFormCities] = useState([]);
  const [formSelectedCountryId, setFormSelectedCountryId] = useState(null);
  const [hasScrapedAddress, setHasScrapedAddress] = useState(false);
  const [mediaUploadRef, setMediaUploadRef] = useState(null);
  const [videoStoryUploadRef, setVideoStoryUploadRef] = useState(null);
  const [currentRaEventId, setCurrentRaEventId] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Only countries that have at least one enabled city
  const activeCountries = countries.filter(c =>
    c.cities?.some(city => city.isEnabled)
  );

  // ── City filter handlers ──
  const handleCountryChange = (countryId) => {
    setSelectedCountryId(countryId);
    setSelectedCityId(null);
    setProfiles([]);
    setSyncResults({});
    const country = countries.find(c => c._id === countryId);
    setCities((country?.cities || []).filter(city => city.isEnabled));
  };

  const handleLoadProfiles = async () => {
    if (!selectedCityId) {
      message.warning('Please select a city');
      return;
    }

    try {
      setLoadingProfiles(true);
      setSyncResults({});

      const result = await fetchProfiles(true, 1, 500, true, undefined, undefined, 'EM');
      const allProfiles = result?.data?.data || result?.data || [];

      const emProfiles = allProfiles.filter(p => {
        const profileCityId = typeof p.city === 'object' ? p.city?._id : p.city;
        return profileCityId === selectedCityId;
      });

      const enriched = await Promise.all(
        emProfiles.map(async (p) => {
          try {
            const full = await getProfileById(p._id);
            return { ...p, scrapSource: full?.scrapSource || null };
          } catch {
            return p;
          }
        })
      );

      setProfiles(enriched);
      message.success(`Found ${enriched.length} venue profiles`);
    } catch (error) {
      message.error('Failed to load profiles');
      console.error(error);
    } finally {
      setLoadingProfiles(false);
    }
  };

  // ── Sync handlers ──
  const handleSync = async (profile) => {
    const sourceType = detectSourceType(profile.scrapSource);
    try {
      setSyncingProfileId(profile._id);
      let result;
      if (sourceType === 'tixr') {
        const tixrData = await fetchTixrGroupEvents(profile.scrapSource);
        result = {
          totalFromSource: tixrData.totalEvents || tixrData.events?.length || 0,
          existingCount: 0,
          missingCount: tixrData.totalEvents || tixrData.events?.length || 0,
          existing: [],
          missing: (tixrData.events || []).map(e => ({ ...e, raEventId: e.url, status: 'missing' })),
          message: `Found ${tixrData.totalEvents || 0} events from Tixr`
        };
      } else {
        result = await rescrapeProfile(profile._id);
      }
      setSyncResults(prev => ({ ...prev, [profile._id]: result }));
      message.success(`${result.message || `Found ${result.totalFromSource} events`}`);
    } catch (error) {
      message.error(error.response?.data?.message || error.message || 'Sync failed');
    } finally {
      setSyncingProfileId(null);
    }
  };

  const handleSaveSource = async (profileId) => {
    if (!editSourceUrl.trim()) {
      message.warning('Please enter a valid URL');
      return;
    }
    try {
      setSavingSource(true);
      await updateProfileScrapSource(profileId, editSourceUrl.trim());
      setProfiles(prev => prev.map(p =>
        p._id === profileId ? { ...p, scrapSource: editSourceUrl.trim() } : p
      ));
      setEditingProfileId(null);
      setEditSourceUrl('');
      message.success('Scrap source updated');
    } catch (error) {
      message.error('Failed to update scrap source');
    } finally {
      setSavingSource(false);
    }
  };

  const toggleSkipProfile = (profileId) => {
    setSkippedProfiles(prev => {
      const next = new Set(prev);
      if (next.has(profileId)) {
        next.delete(profileId);
      } else {
        next.add(profileId);
      }
      return next;
    });
  };

  const handleSyncAll = async () => {
    const profilesToSync = profiles.filter(p => p.scrapSource && !skippedProfiles.has(p._id));
    if (profilesToSync.length === 0) {
      message.warning('No profiles to sync (all skipped or no source)');
      return;
    }
    setSyncingAll(true);
    setSyncAllProgress({ current: 0, total: profilesToSync.length });
    for (let i = 0; i < profilesToSync.length; i++) {
      setSyncAllProgress({ current: i + 1, total: profilesToSync.length });
      try { await handleSync(profilesToSync[i]); } catch (e) { console.error(e); }
      if (i < profilesToSync.length - 1) await new Promise(r => setTimeout(r, 2000));
    }
    setSyncingAll(false);
    message.success(`Sync complete! Synced ${profilesToSync.length} profiles`);
  };

  // ── Event Form: profile search handlers ──
  const handleFormProfileSearch = useCallback(async (value) => {
    if (!searchProfiles || !value?.trim()) return;
    setFormSearchLoading(true);
    try {
      const results = await searchProfiles(value.trim());
      setFormSearchResults(results || []);
    } catch { /* ignore */ }
    finally { setFormSearchLoading(false); }
  }, [searchProfiles]);

  const handleFormProfileSelect = (value) => {
    if (!value) { setFormSelectedProfile(null); return; }
    const selected = formSearchResults.find(p => p._id === value);
    setFormSelectedProfile(selected);
  };

  // ── Event Form: country/city handlers ──
  const handleFormCountryChange = (countryId) => {
    const country = countries.find(c => c._id === countryId);
    if (country) {
      setFormSelectedCountryId(countryId);
      setFormCities(country.cities || []);
      form.setFieldsValue({ city: null });
    }
  };

  const handleFormAddressChange = (addressData) => {
    form.setFieldsValue({ localisation: addressData.localisation || addressData });
  };

  // ── Event Form: AI description ──
  const generateAIDescription = async () => {
    try {
      setAiLoading(true);
      const currentValues = form.getFieldsValue();
      const keywords = [
        currentValues.title, currentValues.eventType,
        ...(currentValues.musicTypes || []), ...(currentValues.tags || []),
        currentValues.organisationName
      ].filter(Boolean).join(', ');
      const venue = currentValues.localisation?.address || '';
      const response = await axios.get('https://mysway.app/api/ai/generate-event-description', {
        params: { keywords: keywords || 'electronic music event', venue, eventType: currentValues.eventType || 'Club' }
      });
      if (response.data?.description) {
        form.setFieldsValue({ description: response.data.description });
        message.success('AI description generated!');
      }
    } catch { message.error('Failed to generate AI description'); }
    finally { setAiLoading(false); }
  };

  // ── Import: scrape inline and show form ──
  const handleImportEvent = async (event, profile) => {
    try {
      setImportingEventUrl(event.url);
      setFormLoading(true);

      const scraperType = detectSourceType(event.url);
      let endpoint = '/scrape';
      if (scraperType === 'tixr') endpoint = '/tixr/scrape';

      const raEventId = event.raEventId || null;
      setCurrentRaEventId(raEventId);

      // Scrape the event
      const response = await axios.post(`http://localhost:3002${endpoint}`, { url: event.url });

      // Map genres
      const scrapedGenres = response.data.genres || response.data.lineup || [];
      const genreNameToId = {};
      musicGenres.forEach(g => { genreNameToId[g.value.toLowerCase()] = g._id; });
      let musicTypeIds = scrapedGenres.map(g => genreNameToId[g.toLowerCase()]).filter(Boolean);
      if (musicTypeIds.length === 0 && profile?.musicTypes?.length > 0) {
        musicTypeIds = profile.musicTypes.map(mt => typeof mt === 'object' ? mt._id : mt);
      }

      // Process dates
      let startTime = response.data.dateTime ? moment(response.data.dateTime) : null;
      if (startTime && !startTime.isValid()) startTime = moment();
      let endTime = response.data.endDateTime ? moment(response.data.endDateTime) : null;
      if (endTime && !endTime.isValid()) endTime = startTime?.clone().add(6, 'hours') || null;
      if (!endTime && startTime) endTime = startTime.clone().add(6, 'hours');

      // Set creator profile
      setFormSelectedProfile(profile);
      setFormSearchResults(prev => {
        if (!prev.find(p => p._id === profile._id)) return [profile, ...prev];
        return prev;
      });

      // Set country/city from the sync tab selection
      const selectedCountry = countries.find(c => c._id === selectedCountryId);
      if (selectedCountry) {
        setFormSelectedCountryId(selectedCountryId);
        setFormCities(selectedCountry.cities || []);
      }

      // Auto-match tags to affiliated profiles
      const tags = response.data.lineup || [];
      let affiliatedProfileIds = [];
      let matchedProfiles = [];
      if (tags.length > 0) {
        const matchResults = await Promise.all(
          tags.map(async (tag) => {
            try {
              const results = await searchProfilesMeili(tag, { isActive: true, limit: 5 });
              const hits = results?.data || [];
              // Find exact or close match by username
              const match = hits.find(h => h.username?.toLowerCase() === tag.toLowerCase())
                || hits.find(h => h.username?.toLowerCase().includes(tag.toLowerCase()));
              return match || null;
            } catch { return null; }
          })
        );
        matchResults.forEach(m => {
          if (m && !affiliatedProfileIds.includes(m._id) && m._id !== profile._id) {
            affiliatedProfileIds.push(m._id);
            matchedProfiles.push(m);
          }
        });
        // Add matched profiles to search results so the Select can display them
        if (matchedProfiles.length > 0) {
          setFormSearchResults(prev => {
            const existing = new Set(prev.map(p => p._id));
            const newOnes = matchedProfiles.filter(p => !existing.has(p._id));
            return [...prev, ...newOnes];
          });
          console.log(`🔗 Auto-matched ${matchedProfiles.length} affiliated profiles:`, matchedProfiles.map(p => p.username));
        }
      }

      // Fill the form
      form.setFieldsValue({
        title: response.data.name,
        description: response.data.description,
        organisationName: response.data.promoter || response.data.organizer || '',
        tags,
        startTime: startTime ? dayjs(startTime.format('YYYY-MM-DD HH:mm:ss')) : null,
        endTime: endTime ? dayjs(endTime.format('YYYY-MM-DD HH:mm:ss')) : null,
        publishTime: dayjs(),
        cover: response.data.image,
        flyer: response.data.image,
        musicTypes: musicTypeIds,
        eventType: 'Club',
        media: [],
        musicList: [],
        videoStory: '',
        embedmix: '',
        youtubeSet: '',
        externalLink: response.data.ticketLink || event.url,
        localisation: { address: response.data.address || response.data.location || '', coordinates: [0, 0] },
        creatorProfileId: profile._id,
        raEventId: raEventId || '',
        country: selectedCountryId || null,
        city: selectedCityId || null,
        affiliatedProfiles: affiliatedProfileIds,
      });

      setHasScrapedAddress(Boolean(response.data.address || response.data.location));
      setShowEventForm(true);
      message.success(`Event "${response.data.name}" scraped!`);
    } catch (error) {
      message.error('Failed to scrape event: ' + (error.response?.data?.message || error.message));
    } finally {
      setFormLoading(false);
      setImportingEventUrl(null);
    }
  };

  // ── Create event ──
  const handleCreateEvent = async (values) => {
    try {
      setFormLoading(true);

      // Upload media
      let mediaUrls = [];
      if (mediaUploadRef?.uploadPendingFiles) {
        mediaUrls = await mediaUploadRef.uploadPendingFiles();
        mediaUrls = [...new Set(mediaUrls.filter(Boolean))];
      }

      // Upload video story
      let videoStoryUrl = values.videoStory || '';
      if (videoStoryUploadRef?.uploadPendingFiles) {
        const urls = await videoStoryUploadRef.uploadPendingFiles();
        if (urls.length > 0) videoStoryUrl = urls[0];
      }

      // Upload flyer from external URL
      let flyerUrl = values.flyer || '';
      if (flyerUrl && !flyerUrl.includes('api.decadance.app')) {
        try {
          const uploadResponse = await apiClient.post('/uploads/from-url', {
            imageUrl: flyerUrl, timestamp: Date.now(), _cacheBust: Math.random()
          });
          if (uploadResponse.data?.data?.url) flyerUrl = uploadResponse.data.data.url;
        } catch (uploadErr) {
          message.error(`Failed to upload flyer: ${uploadErr.response?.statusText || uploadErr.message}`);
          setFormLoading(false);
          return;
        }
      }

      const eventPayload = {
        ...values,
        isPublished: true,
        localisation: values.localisation || { coordinates: [0, 0], address: '' },
        isPrivate: false,
        includeChat: true,
        startTime: values.startTime?.toISOString(),
        endTime: values.endTime?.toISOString(),
        publishTime: values.publishTime?.toISOString(),
        musicTypes: Array.isArray(values.musicTypes) ? values.musicTypes : [],
        tags: Array.isArray(values.tags) ? values.tags : [],
        affiliatedProfiles: Array.isArray(values.affiliatedProfiles) ? values.affiliatedProfiles : [],
        media: [...new Set([...(Array.isArray(values.media) ? values.media : []), ...mediaUrls])],
        musicList: Array.isArray(values.musicList) ? values.musicList : [],
        flyer: flyerUrl,
        videoStory: videoStoryUrl,
        externalLink: values.externalLink || '',
        youtubeSet: values.youtubeSet || '',
        embedmix: values.embedmix || '',
        eventType: values.eventType || 'Club',
        description: values.description || '',
        organisationName: values.organisationName || '',
        cover: values.cover || null,
        raEventId: values.raEventId || currentRaEventId || null,
        unfinished: values.unfinished || false,
      };

      if (!eventPayload.youtubeSet?.trim()) delete eventPayload.youtubeSet;
      if (!eventPayload.externalLink?.trim()) delete eventPayload.externalLink;
      if (!eventPayload.embedmix?.trim()) delete eventPayload.embedmix;
      if (eventPayload.localisation?.localisation) eventPayload.localisation = eventPayload.localisation.localisation;

      await apiClient.post('/backoffice/events/create', eventPayload);
      message.success('Event created successfully!');
      setShowEventForm(false);
      form.resetFields();
      setFormSelectedProfile(null);
      setCurrentRaEventId(null);
      setMediaUploadRef(null);
      setVideoStoryUploadRef(null);
    } catch (error) {
      message.error(`Failed to create event: ${error.response?.data?.message || error.message}`);
    } finally {
      setFormLoading(false);
    }
  };

  // ── Fast Import: scrape + create in one shot ──
  const handleFastImport = async (event, profile, { unfinished = false } = {}) => {
    try {
      if (unfinished) setFastUnfinishedEventUrl(event.url);
      else setFastImportingEventUrl(event.url);

      const scraperType = detectSourceType(event.url);
      let endpoint = '/scrape';
      if (scraperType === 'tixr') endpoint = '/tixr/scrape';

      const raEventId = event.raEventId || null;

      // Scrape the event
      const response = await axios.post(`http://localhost:3002${endpoint}`, { url: event.url });

      // Map genres
      const scrapedGenres = response.data.genres || response.data.lineup || [];
      const genreNameToId = {};
      musicGenres.forEach(g => { genreNameToId[g.value.toLowerCase()] = g._id; });
      let musicTypeIds = scrapedGenres.map(g => genreNameToId[g.toLowerCase()]).filter(Boolean);
      if (musicTypeIds.length === 0 && profile?.musicTypes?.length > 0) {
        musicTypeIds = profile.musicTypes.map(mt => typeof mt === 'object' ? mt._id : mt);
      }

      // Process dates
      let startTime = response.data.dateTime ? moment(response.data.dateTime) : null;
      if (startTime && !startTime.isValid()) startTime = moment();
      let endTime = response.data.endDateTime ? moment(response.data.endDateTime) : null;
      if (endTime && !endTime.isValid()) endTime = startTime?.clone().add(6, 'hours') || null;
      if (!endTime && startTime) endTime = startTime.clone().add(6, 'hours');

      // Upload flyer from external URL
      let flyerUrl = response.data.image || '';
      if (flyerUrl && !flyerUrl.includes('api.decadance.app')) {
        try {
          const uploadResponse = await apiClient.post('/uploads/from-url', {
            imageUrl: flyerUrl, timestamp: Date.now(), _cacheBust: Math.random()
          });
          if (uploadResponse.data?.data?.url) flyerUrl = uploadResponse.data.data.url;
        } catch (uploadErr) {
          console.warn('Flyer upload failed, using original URL:', uploadErr.message);
        }
      }

      // Auto-match tags to affiliated profiles
      const tags = response.data.lineup || [];
      let affiliatedProfileIds = [];
      if (tags.length > 0) {
        const matchResults = await Promise.all(
          tags.map(async (tag) => {
            try {
              const results = await searchProfilesMeili(tag, { isActive: true, limit: 5 });
              const hits = results?.data || [];
              const match = hits.find(h => h.username?.toLowerCase() === tag.toLowerCase())
                || hits.find(h => h.username?.toLowerCase().includes(tag.toLowerCase()));
              return match || null;
            } catch { return null; }
          })
        );
        matchResults.forEach(m => {
          if (m && !affiliatedProfileIds.includes(m._id) && m._id !== profile._id) {
            affiliatedProfileIds.push(m._id);
          }
        });
        if (affiliatedProfileIds.length > 0) {
          console.log(`🔗 Fast import: auto-matched ${affiliatedProfileIds.length} affiliated profiles`);
        }
      }

      // Build and submit payload directly
      const eventPayload = {
        title: response.data.name,
        description: response.data.description || '',
        organisationName: response.data.promoter || response.data.organizer || '',
        tags,
        startTime: startTime?.toISOString(),
        endTime: endTime?.toISOString(),
        publishTime: new Date().toISOString(),
        cover: flyerUrl || null,
        flyer: flyerUrl,
        musicTypes: musicTypeIds,
        eventType: 'Club',
        media: [],
        musicList: [],
        videoStory: '',
        externalLink: response.data.ticketLink || event.url || '',
        localisation: { address: response.data.address || response.data.location || '', coordinates: [0, 0] },
        creatorProfileId: profile._id,
        raEventId: raEventId || null,
        country: selectedCountryId || null,
        city: selectedCityId || null,
        isPublished: true,
        isPrivate: false,
        includeChat: true,
        affiliatedProfiles: affiliatedProfileIds,
        unfinished: unfinished,
      };

      if (!eventPayload.externalLink?.trim()) delete eventPayload.externalLink;

      await apiClient.post('/backoffice/events/create', eventPayload);
      message.success(`${unfinished ? 'Fast U' : 'Fast'} imported: "${response.data.name}"`);

      // Move event from missing to existing in sync results
      setSyncResults(prev => {
        const profileResults = prev[profile._id];
        if (!profileResults) return prev;
        const importedEvent = profileResults.missing?.find(e => e.url === event.url);
        return {
          ...prev,
          [profile._id]: {
            ...profileResults,
            missing: (profileResults.missing || []).filter(e => e.url !== event.url),
            existing: [...(profileResults.existing || []), ...(importedEvent ? [{ ...importedEvent, status: 'existing', _status: 'existing' }] : [])],
            missingCount: (profileResults.missingCount || 0) - 1,
            existingCount: (profileResults.existingCount || 0) + 1,
          }
        };
      });
    } catch (error) {
      message.error('Fast import failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setFastImportingEventUrl(null);
      setFastUnfinishedEventUrl(null);
    }
  };

  // ── Skip/unskip event ──
  const toggleSkipEvent = (eventKey) => {
    setSkippedEvents(prev => {
      const next = new Set(prev);
      if (next.has(eventKey)) {
        next.delete(eventKey);
      } else {
        next.add(eventKey);
      }
      return next;
    });
  };

  // ── Fast All: import all missing events with delay ──
  const handleFastAll = async (unfinished = false) => {
    const missingEvents = allSyncedEvents.filter(e => e._status === 'missing' && !skippedEvents.has(e._key));
    if (missingEvents.length === 0) {
      message.warning('No missing events to import (all skipped or none)');
      return;
    }
    setFastAllRunning(true);
    setFastAllMode(unfinished ? 'unfinished' : 'publish');
    setFastAllProgress({ current: 0, total: missingEvents.length });

    let successCount = 0;
    for (let i = 0; i < missingEvents.length; i++) {
      setFastAllProgress({ current: i + 1, total: missingEvents.length });
      try {
        await handleFastImport(missingEvents[i], missingEvents[i]._profile, { unfinished });
        successCount++;
      } catch (e) {
        console.error('Fast All error on event:', missingEvents[i]._title, e);
      }
      if (i < missingEvents.length - 1) await new Promise(r => setTimeout(r, 2500));
    }

    setFastAllRunning(false);
    setFastAllMode(null);
    message.success(`Fast All done! Imported ${successCount}/${missingEvents.length} events${unfinished ? ' (unfinished)' : ''}`);
  };

  // ── Filter and sort profiles ──
  const filteredProfiles = React.useMemo(() => {
    let list = profiles;
    if (profileSearch.trim()) {
      const q = profileSearch.toLowerCase();
      list = list.filter(p =>
        p.username?.toLowerCase().includes(q) ||
        p.scrapSource?.toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      if (a.scrapSource && !b.scrapSource) return -1;
      if (!a.scrapSource && b.scrapSource) return 1;
      return (a.username || '').localeCompare(b.username || '');
    });
  }, [profiles, profileSearch]);

  // ── Aggregated sync results ──
  const allSyncedEvents = React.useMemo(() => {
    const events = [];
    Object.entries(syncResults).forEach(([profileId, result]) => {
      const profile = profiles.find(p => p._id === profileId);
      if (!profile) return;
      (result.existing || []).forEach(event => {
        events.push({ ...event, _profile: profile, _status: 'existing', _title: event.raTitle || event.dbTitle || event.title || '', _date: event.raDate || event.date || '', _key: `${profileId}-existing-${event.raEventId}` });
      });
      (result.missing || []).forEach(event => {
        events.push({ ...event, _profile: profile, _status: 'missing', _title: event.title || '', _date: event.date || '', _key: `${profileId}-missing-${event.raEventId}` });
      });
    });
    events.sort((a, b) => {
      const da = a._date ? new Date(a._date) : 0;
      const db = b._date ? new Date(b._date) : 0;
      return db - da;
    });
    return events;
  }, [syncResults, profiles]);

  const filteredEvents = React.useMemo(() => {
    let list = allSyncedEvents;
    if (showOnlyMissing) list = list.filter(e => e._status === 'missing');
    if (eventSearch.trim()) {
      const q = eventSearch.toLowerCase();
      list = list.filter(e =>
        e._title.toLowerCase().includes(q) ||
        e._profile?.username?.toLowerCase().includes(q) ||
        (e.artists || []).some(a => a.toLowerCase().includes(q))
      );
    }
    return list;
  }, [allSyncedEvents, showOnlyMissing, eventSearch]);

  const totalMissing = allSyncedEvents.filter(e => e._status === 'missing').length;
  const totalExisting = allSyncedEvents.filter(e => e._status === 'existing').length;
  const totalSkipped = allSyncedEvents.filter(e => e._status === 'missing' && skippedEvents.has(e._key)).length;
  const totalUnskippedMissing = totalMissing - totalSkipped;

  // ── Table columns ──
  const profileColumns = [
    {
      title: 'Profile', key: 'profile', width: 250,
      render: (_, record) => (
        <Space>
          <Avatar src={record.picture} icon={<UserOutlined />} size={40} />
          <div>
            <div className="font-medium">{record.username}</div>
            <div className="text-xs text-gray-400">{record.profileType}</div>
          </div>
        </Space>
      )
    },
    {
      title: 'Scrap Source', key: 'scrapSource', ellipsis: true,
      render: (_, record) => {
        if (editingProfileId === record._id) {
          return (
            <Space.Compact style={{ width: '100%' }}>
              <Input placeholder="https://ra.co/clubs/..." value={editSourceUrl} onChange={(e) => setEditSourceUrl(e.target.value)} onPressEnter={() => handleSaveSource(record._id)} />
              <Button type="primary" loading={savingSource} onClick={() => handleSaveSource(record._id)}>Save</Button>
              <Button onClick={() => { setEditingProfileId(null); setEditSourceUrl(''); }}>Cancel</Button>
            </Space.Compact>
          );
        }
        if (record.scrapSource) {
          const type = detectSourceType(record.scrapSource);
          return (
            <Space>
              <Tag color={type === 'ra' ? 'purple' : type === 'tixr' ? 'blue' : 'default'}>{type.toUpperCase()}</Tag>
              <a href={record.scrapSource} target="_blank" rel="noreferrer" className="text-blue-500 text-xs">{record.scrapSource}</a>
              <Tooltip title="Edit source">
                <Button icon={<EditOutlined />} size="small" type="text" onClick={() => { setEditingProfileId(record._id); setEditSourceUrl(record.scrapSource); }} />
              </Tooltip>
            </Space>
          );
        }
        return <Tag color="default">Not set</Tag>;
      }
    },
    {
      title: 'Actions', key: 'actions', width: 250,
      render: (_, record) => (
        <Space>
          {record.scrapSource ? (
            <>
              {!skippedProfiles.has(record._id) ? (
                <>
                  <Button type="primary" icon={<SyncOutlined spin={syncingProfileId === record._id} />} loading={syncingProfileId === record._id} onClick={() => handleSync(record)}>Sync</Button>
                  <Tooltip title="Skip from Sync All">
                    <Button icon={<StopOutlined />} size="small" style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }} onClick={() => toggleSkipProfile(record._id)}>Skip</Button>
                  </Tooltip>
                </>
              ) : (
                <Button icon={<UndoOutlined />} size="small" type="dashed" onClick={() => toggleSkipProfile(record._id)}>Unskip</Button>
              )}
            </>
          ) : (
            <Button icon={<PlusOutlined />} onClick={() => { setEditingProfileId(record._id); setEditSourceUrl(''); }}>Set Source</Button>
          )}
        </Space>
      )
    },
  ];

  const eventColumns = [
    {
      title: 'Venue', key: 'venue', width: 160,
      render: (_, record) => (
        <Space>
          <Avatar src={record._profile?.picture} icon={<UserOutlined />} size={28} />
          <span className="text-xs font-medium">{record._profile?.username}</span>
        </Space>
      ),
    },
    {
      title: 'Date', key: 'date', width: 110,
      sorter: (a, b) => {
        const da = a._date ? new Date(a._date).getTime() : 0;
        const db = b._date ? new Date(b._date).getTime() : 0;
        return da - db;
      },
      defaultSortOrder: 'ascend',
      render: (_, record) => (
        <span className="text-xs">{record._date ? moment(record._date).format('DD MMM YYYY') : '—'}</span>
      ),
    },
    {
      title: 'Event', key: 'event',
      render: (_, record) => (
        <div className="flex items-center gap-3">
          {record.image && <img src={record.image} alt="" className="w-10 h-10 object-cover rounded" />}
          <div>
            <div className="font-medium">{record._title}</div>
            {record.artists?.length > 0 && (
              <div className="text-xs text-gray-500">{record.artists.join(', ')}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Status', key: 'status', width: 100,
      render: (_, record) => {
        if (record._status === 'existing') return <Tag color="success">In DB</Tag>;
        if (skippedEvents.has(record._key)) return <Tag color="default">Skipped</Tag>;
        return <Tag color="warning">Missing</Tag>;
      },
    },
    {
      title: 'Actions', key: 'actions', width: 340,
      render: (_, record) => (
        <Space>
          {record.url && (
            <Tooltip title="Open link">
              <Button icon={<LinkOutlined />} size="small" onClick={() => window.open(record.url, '_blank')} />
            </Tooltip>
          )}
          {record._status === 'missing' && !skippedEvents.has(record._key) && (
            <>
              <Button type="primary" icon={<PlusOutlined />} size="small" loading={importingEventUrl === record.url} onClick={() => handleImportEvent(record, record._profile)}>
                Import
              </Button>
              <Tooltip title="Scrape & create instantly">
                <Button type="primary" ghost icon={<ThunderboltOutlined />} size="small" loading={fastImportingEventUrl === record.url} onClick={() => handleFastImport(record, record._profile)}>
                  Fast
                </Button>
              </Tooltip>
              <Tooltip title="Fast import as unfinished">
                <Button type="default" icon={<WarningOutlined />} size="small" style={{ borderColor: '#faad14', color: '#faad14' }} loading={fastUnfinishedEventUrl === record.url} onClick={() => handleFastImport(record, record._profile, { unfinished: true })}>
                  Fast U
                </Button>
              </Tooltip>
              <Tooltip title="Skip this event from batch imports">
                <Button icon={<StopOutlined />} size="small" style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }} onClick={() => toggleSkipEvent(record._key)}>
                  Skip
                </Button>
              </Tooltip>
            </>
          )}
          {record._status === 'missing' && skippedEvents.has(record._key) && (
            <Tooltip title="Unskip this event">
              <Button icon={<UndoOutlined />} size="small" type="dashed" onClick={() => toggleSkipEvent(record._key)}>
                Unskip
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* City Filter */}
      <Card
        title={<div className="flex items-center gap-2"><SyncOutlined className="text-blue-500" /><span>Event Sync</span></div>}
        className="shadow-lg"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <Select value={selectedCountryId} onChange={handleCountryChange} style={{ width: '100%' }} size="large" placeholder="Select a country" showSearch optionFilterProp="children">
              {activeCountries.map(country => (<Option key={country._id} value={country._id}>{country.name}</Option>))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <Select value={selectedCityId} onChange={setSelectedCityId} style={{ width: '100%' }} size="large" placeholder="Select a city" disabled={!selectedCountryId} showSearch optionFilterProp="children">
              {cities.map(city => (<Option key={city._id} value={city._id}>{city.name}</Option>))}
            </Select>
          </div>
          <div className="flex items-end">
            <Button type="primary" icon={<CloudDownloadOutlined />} size="large" loading={loadingProfiles} onClick={handleLoadProfiles} disabled={!selectedCityId}>
              Load Profiles
            </Button>
          </div>
        </div>
      </Card>

      {/* Event Form — inline on this page */}
      {showEventForm && (
        <Row gutter={24}>
          <EventFormSection
            form={form}
            loading={formLoading}
            handleCreateEvent={handleCreateEvent}
            setShowEventForm={setShowEventForm}
            setHasScrapedAddress={setHasScrapedAddress}
            setMediaUploadRef={setMediaUploadRef}
            setVideoStoryUploadRef={setVideoStoryUploadRef}
            selectedProfile={formSelectedProfile}
            searchResults={formSearchResults}
            profilesLoading={externalProfilesLoading}
            searchLoading={formSearchLoading}
            handleProfileSearch={handleFormProfileSearch}
            handleProfileSelect={handleFormProfileSelect}
            countries={countries}
            cities={formCities}
            handleCountryChange={handleFormCountryChange}
            handleAddressChange={handleFormAddressChange}
            hasScrapedAddress={hasScrapedAddress}
            musicGenres={musicGenres}
            aiLoading={aiLoading}
            generateAIDescription={generateAIDescription}
            raEventId={currentRaEventId}
          />
        </Row>
      )}

      {/* Sync Results */}
      {allSyncedEvents.length > 0 && (
        <Card
          title={
            <div className="flex items-center gap-2">
              <ExclamationCircleOutlined className="text-orange-500" />
              <span>Sync Results</span>
              <Tag color="warning">{totalMissing} missing</Tag>
              <Tag color="success">{totalExisting} in DB</Tag>
              {totalSkipped > 0 && <Tag color="default">{totalSkipped} skipped</Tag>}
            </div>
          }
          className="shadow-lg"
        >
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <Input placeholder="Search events..." prefix={<SearchOutlined />} value={eventSearch} onChange={(e) => setEventSearch(e.target.value)} allowClear style={{ maxWidth: 300 }} />
            <Button type={showOnlyMissing ? 'primary' : 'default'} onClick={() => setShowOnlyMissing(!showOnlyMissing)}>
              {showOnlyMissing ? 'Show All' : 'Missing Only'}
            </Button>
            <div style={{ marginLeft: 'auto' }} className="flex items-center gap-2">
              {fastAllRunning && (
                <Progress percent={Math.round((fastAllProgress.current / fastAllProgress.total) * 100)} size="small" style={{ width: 120 }} format={() => `${fastAllProgress.current}/${fastAllProgress.total}`} />
              )}
              <Tooltip title={`Fast import ${totalUnskippedMissing} missing events${totalSkipped > 0 ? ` (${totalSkipped} skipped)` : ''}`}>
                <Button type="primary" icon={<ThunderboltOutlined />} loading={fastAllRunning && fastAllMode === 'publish'} disabled={fastAllRunning || totalUnskippedMissing === 0} onClick={() => handleFastAll(false)}>
                  Fast All ({totalUnskippedMissing})
                </Button>
              </Tooltip>
              <Tooltip title={`Fast import ${totalUnskippedMissing} as unfinished${totalSkipped > 0 ? ` (${totalSkipped} skipped)` : ''}`}>
                <Button icon={<WarningOutlined />} style={{ borderColor: '#faad14', color: '#faad14' }} loading={fastAllRunning && fastAllMode === 'unfinished'} disabled={fastAllRunning || totalUnskippedMissing === 0} onClick={() => handleFastAll(true)}>
                  Fast All U ({totalUnskippedMissing})
                </Button>
              </Tooltip>
            </div>
          </div>
          <Table columns={eventColumns} dataSource={filteredEvents} rowKey="_key" pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (total) => `${total} events` }} size="small" />
        </Card>
      )}

      {/* Profiles Table */}
      {profiles.length > 0 && (
        <Card
          title={<div className="flex items-center gap-2"><GlobalOutlined className="text-green-500" /><span>Venue Profiles ({profiles.length})</span></div>}
          extra={
            <Space>
              {syncingAll && (
                <Progress percent={Math.round((syncAllProgress.current / syncAllProgress.total) * 100)} size="small" style={{ width: 150 }} format={() => `${syncAllProgress.current}/${syncAllProgress.total}`} />
              )}
              <Button type="primary" icon={<SyncOutlined spin={syncingAll} />} loading={syncingAll} onClick={handleSyncAll} disabled={!profiles.some(p => p.scrapSource && !skippedProfiles.has(p._id))}>
                Sync All ({profiles.filter(p => p.scrapSource && !skippedProfiles.has(p._id)).length})
              </Button>
            </Space>
          }
          className="shadow-lg"
        >
          <Input placeholder="Search profiles..." prefix={<SearchOutlined />} value={profileSearch} onChange={(e) => setProfileSearch(e.target.value)} allowClear style={{ marginBottom: 12, maxWidth: 300 }} />
          <Table columns={profileColumns} dataSource={filteredProfiles} rowKey="_id" pagination={{ pageSize: 50 }} size="small" />
        </Card>
      )}

      {/* Empty state */}
      {!loadingProfiles && profiles.length === 0 && selectedCityId && (
        <Card className="shadow-lg">
          <Alert message="No Profiles" description="No venue profiles found for this city. Click 'Load Profiles' to search." type="info" showIcon />
        </Card>
      )}
    </div>
  );
};

export default EventSyncTab;
